import React, { useMemo, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Feather } from "@expo/vector-icons";

import { CORES, sombraCard } from "../../../template/Etapas/styles";
import { corTextoSobre } from "../../../theme/cores";

// Calendário do layout novo. O componente antigo (src/components/Calendario)
// continua sendo usado pelo modo legado.

interface Props {
  dataSelecionada: Date | null;
  diasAtendimento: number[]; // dias da semana atendidos (0 = domingo)
  corPrincipal: string;
  onChange: (data: Date) => void;
}

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const DIAS_SEMANA = ["SEG", "TER", "QUA", "QUI", "SEX", "SÁB", "DOM"];

function mesmoDia(a: Date | null, b: Date) {
  return (
    !!a &&
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear()
  );
}

// Monta as semanas do mês (segunda a domingo), completando com dias dos meses vizinhos
function montarSemanas(ano: number, mes: number): Date[][] {
  const primeiroDia = new Date(ano, mes, 1);
  const ultimoDia = new Date(ano, mes + 1, 0).getDate();
  const deslocamento = (primeiroDia.getDay() + 6) % 7; // segunda = 0

  const dias: Date[] = [];
  for (let i = deslocamento; i > 0; i--) {
    dias.push(new Date(ano, mes, 1 - i));
  }
  for (let i = 1; i <= ultimoDia; i++) {
    dias.push(new Date(ano, mes, i));
  }
  let proximo = 1;
  while (dias.length % 7 !== 0) {
    dias.push(new Date(ano, mes + 1, proximo++));
  }

  const semanas: Date[][] = [];
  for (let i = 0; i < dias.length; i += 7) {
    semanas.push(dias.slice(i, i + 7));
  }
  return semanas;
}

export default function CalendarioNovo({
  dataSelecionada,
  diasAtendimento,
  corPrincipal,
  onChange,
}: Props) {
  const hoje = new Date();
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());

  const [mesExibindo, setMesExibindo] = useState<Date>(
    new Date(
      (dataSelecionada || hoje).getFullYear(),
      (dataSelecionada || hoje).getMonth(),
      1,
    ),
  );

  const semanas = useMemo(
    () => montarSemanas(mesExibindo.getFullYear(), mesExibindo.getMonth()),
    [mesExibindo],
  );

  const podeVoltarMes =
    mesExibindo.getFullYear() > hoje.getFullYear() ||
    (mesExibindo.getFullYear() === hoje.getFullYear() &&
      mesExibindo.getMonth() > hoje.getMonth());

  function mudarMes(quantidade: number) {
    setMesExibindo(
      new Date(mesExibindo.getFullYear(), mesExibindo.getMonth() + quantidade, 1),
    );
  }

  function renderDia(data: Date) {
    const doMes = data.getMonth() === mesExibindo.getMonth();
    const passado = data < inicioHoje;
    const fechado = !diasAtendimento.includes(data.getDay());
    const disponivel = doMes && !passado && !fechado;
    const selecionado = doMes && mesmoDia(dataSelecionada, data);

    return (
      <View key={data.toISOString()} style={styles.celula}>
        {doMes && (
          <TouchableOpacity
            disabled={!disponivel}
            onPress={() => onChange(data)}
            style={[
              styles.dia,
              disponivel && styles.diaDisponivel,
              selecionado && {
                backgroundColor: corPrincipal,
                borderColor: corPrincipal,
              },
            ]}
          >
            <Text
              style={[
                styles.textoDia,
                !disponivel && styles.textoDiaIndisponivel,
                fechado && !passado && styles.textoDiaFechado,
                selecionado && {
                  color: corTextoSobre(corPrincipal),
                  fontWeight: "800",
                },
              ]}
            >
              {data.getDate()}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={[styles.card, sombraCard]}>
      {/* Navegação do mês */}
      <View style={styles.cabecalho}>
        <TouchableOpacity
          disabled={!podeVoltarMes}
          onPress={() => mudarMes(-1)}
          style={[styles.botaoMes, !podeVoltarMes && { opacity: 0.35 }]}
        >
          <Feather name="chevron-left" size={18} color={CORES.titulo} />
        </TouchableOpacity>

        <Text style={styles.textoMes}>
          {MESES[mesExibindo.getMonth()]}{" "}
          <Text style={styles.textoAno}>{mesExibindo.getFullYear()}</Text>
        </Text>

        <TouchableOpacity onPress={() => mudarMes(1)} style={styles.botaoMes}>
          <Feather name="chevron-right" size={18} color={CORES.titulo} />
        </TouchableOpacity>
      </View>

      {/* Dias da semana */}
      <View style={styles.linha}>
        {DIAS_SEMANA.map((dia) => (
          <View key={dia} style={styles.celula}>
            <Text style={styles.textoSemana}>{dia}</Text>
          </View>
        ))}
      </View>

      {semanas.map((semana, index) => (
        <View key={index} style={styles.linha}>
          {semana.map((data) => renderDia(data))}
        </View>
      ))}

      {/* Legenda */}
      <View style={styles.legenda}>
        <View style={styles.itemLegenda}>
          <View style={[styles.marcadorLegenda, styles.diaDisponivel]} />
          <Text style={styles.textoLegenda}>Disponível</Text>
        </View>
        <View style={styles.itemLegenda}>
          <View
            style={[styles.marcadorLegenda, { backgroundColor: corPrincipal }]}
          />
          <Text style={styles.textoLegenda}>Selecionado</Text>
        </View>
        <View style={styles.itemLegenda}>
          <Text style={[styles.textoLegenda, styles.textoDiaFechado]}>00</Text>
          <Text style={styles.textoLegenda}>Fechado</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: CORES.card,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: CORES.borda,
    padding: 14,
  },
  cabecalho: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  botaoMes: {
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: CORES.borda,
    alignItems: "center",
    justifyContent: "center",
  },
  textoMes: {
    fontSize: 16,
    fontWeight: "800",
    color: CORES.titulo,
  },
  textoAno: {
    fontWeight: "500",
    color: CORES.subtitulo,
  },
  linha: {
    flexDirection: "row",
  },
  celula: {
    flex: 1,
    aspectRatio: 1,
    padding: 3,
    alignItems: "center",
    justifyContent: "center",
  },
  textoSemana: {
    fontSize: 11,
    fontWeight: "600",
    color: CORES.subtitulo,
  },
  dia: {
    width: "100%",
    height: "100%",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
  },
  diaDisponivel: {
    backgroundColor: CORES.card,
    borderColor: "rgba(0,0,0,0.1)",
  },
  textoDia: {
    fontSize: 14,
    fontWeight: "600",
    color: CORES.titulo,
  },
  textoDiaIndisponivel: {
    color: "#c7c2b8",
    fontWeight: "500",
  },
  textoDiaFechado: {
    textDecorationLine: "line-through",
  },
  legenda: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: CORES.borda,
  },
  itemLegenda: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  marcadorLegenda: {
    width: 10,
    height: 10,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.15)",
  },
  textoLegenda: {
    fontSize: 11,
    color: CORES.subtitulo,
  },
});
